import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const FeaturesPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const features = [
    {
      icon: "🎯",
      title: "Personalized Familiarity Scoring",
      description: "Our advanced algorithm analyzes %Non-US IMG acceptance rates and available positions to calculate personalized compatibility scores for each residency program, helping you identify your best matches.",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: "📊",
      title: "Comprehensive Residency Database",
      description: "Access detailed information on 600+ residency programs across all specialties with IMG-specific data including Step 2 CK cutoffs, visa sponsorship details, and historical acceptance rates.",
      color: "from-teal-500 to-teal-600"
    },
    {
      icon: "🔍",
      title: "Smart Profile-Based Filtering",
      description: "Filter programs based on your USMLE scores, graduation timeline, USCE experience, visa requirements, and country of graduation to find programs that truly match your profile.",
      color: "from-indigo-500 to-indigo-600"
    },
    {
      icon: "📈",
      title: "Step 2 CK Cutoff Analysis",
      description: "Premium feature providing access to Step 2 CK score requirements for programs, helping you make strategic decisions about where to apply based on your actual scores.",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: "💡",
      title: "Strategic Application Planning",
      description: "Get data-driven insights on program composition, IMG-friendliness percentages, and position availability to optimize your application strategy and maximize match chances.",
      color: "from-green-500 to-green-600"
    },
    {
      icon: "⭐",
      title: "Favorites & Comparison Tools",
      description: "Save programs to your favorites list and use detailed comparison views with compatibility charts, program statistics, and comprehensive analysis to make informed decisions.",
      color: "from-orange-500 to-orange-600"
    },
    {
      icon: "🏥",
      title: "USCE & Visa Matching",
      description: "Find programs that match your US Clinical Experience level and visa sponsorship needs (J1 vs H1B), ensuring you only apply to programs that can actually accept you.",
      color: "from-pink-500 to-pink-600"
    },
    {
      icon: "🌍",
      title: "Global Pricing & Accessibility",
      description: "Country-specific pricing in local currencies makes premium features accessible worldwide, with one-time payment providing lifetime access to all specialties and updates.",
      color: "from-cyan-500 to-cyan-600"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
<img 
  src="/images/logo.png" 
  alt="Residency Companion Logo" 
  className="w-10 h-10 mr-4 object-contain"
/>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                Residency Companion
              </h1>
            </Link>
            <div className="flex items-center space-x-1">
              <Link to="/login" className="text-gray-600 hover:text-gray-900 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:bg-gray-50">
                Sign In
              </Link>
              <Link to="/signup" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob-enhanced"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-teal-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob-enhanced animation-delay-2000"></div>
        </div>
        
        <div className={`relative z-10 text-center px-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="max-w-4xl mx-auto">
            <div className="mb-6 inline-flex items-center space-x-2 bg-white/70 backdrop-blur-sm border border-gray-200 rounded-full px-4 py-2 shadow-sm">
              <span className="w-4 h-4 text-blue-600">✨</span>
              <span className="text-sm text-gray-700 font-medium">Smart IMG Residency Matching Platform</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-thin text-gray-900 mb-6 leading-tight">
              Features That Help IMGs
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent font-light">
                Match Successfully
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto font-light leading-relaxed">
              Data-driven tools designed specifically for International Medical Graduates to find and apply to the right residency programs
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Core Features Introduction */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-thin text-gray-900 mb-6">
              Everything You Need for
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent font-light"> Strategic Applications</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our platform combines comprehensive residency data with intelligent analysis to help you make informed decisions about your medical career.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`group bg-white border border-gray-100 rounded-2xl p-8 hover:border-blue-200 transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <span className="text-2xl">{feature.icon}</span>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-gradient-to-r from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-thin text-gray-900 mb-6">
              How Residency Companion Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From profile setup to strategic application planning
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="text-center group">
              <div className="relative mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Setup Your Profile</h3>
              <p className="text-gray-600 leading-relaxed text-sm">
                Enter your USMLE scores, graduation details, USCE experience, and visa requirements to create your personalized medical profile.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center group">
              <div className="relative mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Get Familiarity Scores</h3>
              <p className="text-gray-600 leading-relaxed text-sm">
                Our algorithm calculates compatibility scores using %Non-US IMG data and position availability to rank programs by your match likelihood.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center group">
              <div className="relative mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Filter & Compare</h3>
              <p className="text-gray-600 leading-relaxed text-sm">
                Use smart filters based on your profile criteria and compare programs with detailed analytics, Step 2 cutoffs, and visa sponsorship info.
              </p>
            </div>

            {/* Step 4 */}
            <div className="text-center group">
              <div className="relative mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <span className="text-2xl font-bold text-white">4</span>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Apply Strategically</h3>
              <p className="text-gray-600 leading-relaxed text-sm">
                Save favorites, analyze program composition, and make data-driven decisions about where to invest your application efforts and fees.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Features Highlight */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-3xl p-12 border border-blue-200">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-thin text-gray-900 mb-6">
                Premium Features
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent font-light"> Unlock Full Potential</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Get access to advanced analytics and strategic insights with our premium features
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-white text-xl">📊</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Step 2 CK Cutoffs</h3>
                <p className="text-gray-600 text-sm">Access actual Step 2 CK score requirements for programs to make strategic application decisions.</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-white text-xl">🎯</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Familiarity Scoring</h3>
                <p className="text-gray-600 text-sm">Get personalized compatibility scores for all programs based on your specific profile and statistics.</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-white text-xl">📈</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Complete Analytics</h3>
                <p className="text-gray-600 text-sm">View all 600+ programs, %Non-US IMG data, and comprehensive program composition analysis.</p>
              </div>
            </div>

            <div className="text-center mt-12">
              <Link
                to="/pricing"
                className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-2xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <span className="mr-2">💎</span>
                View Pricing Plans
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-teal-600">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-thin text-white mb-6">
            Ready to Find Your Match?
          </h2>
          <p className="text-xl text-blue-100 mb-8 font-light">
            Join thousands of IMGs who use Residency Companion for strategic residency applications
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="bg-white text-blue-600 px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Start Free Trial
            </Link>
            <Link
              to="/how-it-works"
              className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:bg-white hover:text-blue-600"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-6">
<img 
  src="/images/logo.png" 
  alt="Residency Companion Logo" 
  className="w-12 h-12 mr-4 object-contain"
/>
                <div>
                  <h4 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                    Residency Companion
                  </h4>
                  <p className="text-sm text-gray-500 font-medium">IMG Residency Matching Platform</p>
                </div>
              </div>
              <p className="text-gray-600 font-light">
                Your strategic partner in medical residency applications.
              </p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-4">Product</h5>
              <ul className="space-y-3 text-gray-600">
                <li><Link to="/features" className="text-blue-600 font-medium">Features</Link></li>
                <li><Link to="/how-it-works" className="hover:text-blue-600 transition-colors">How It Works</Link></li>
                <li><Link to="/api" className="hover:text-blue-600 transition-colors">API</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-4">Support</h5>
              <ul className="space-y-3 text-gray-600">
                <li><Link to="/help" className="hover:text-blue-600 transition-colors">Help Center</Link></li>
                <li><Link to="/contact" className="hover:text-blue-600 transition-colors">Contact Us</Link></li>
                <li><Link to="/refund-policy" className="hover:text-blue-600 transition-colors">Refund Policy</Link></li>
                <li><Link to="/cancellation-policy" className="hover:text-blue-600 transition-colors">Cancellation Policy</Link></li>
                <li><Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-blue-600 transition-colors">Terms</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-4">Company</h5>
              <ul className="space-y-3 text-gray-600">
                <li><Link to="/about" className="hover:text-blue-600 transition-colors">About</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-12 pt-8 text-center">
            <p className="text-gray-500 text-sm">
              © 2025 Residency Companion. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes blob-enhanced {
          0% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
          25% { transform: translate(20px, -30px) scale(1.05) rotate(5deg); }
          50% { transform: translate(-15px, 10px) scale(0.95) rotate(-3deg); }
          75% { transform: translate(25px, -20px) scale(1.08) rotate(7deg); }
          100% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
        }
        .animate-blob-enhanced { animation: blob-enhanced 9s ease-in-out infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default FeaturesPage;